import { Button } from "@/components/ui/button"
import { SignalDot } from "@/components/ui/living-brand"
import { Text } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

type CtaSectionProps = {
  id?: string
}

export function CtaSection({ id }: CtaSectionProps) {
  const { t } = useTranslation("marketing")
  const navigate = useNavigate()

  return (
    <section
      id={id}
      className={cn(
        "py-16 sm:py-24",
        "outline-none", // Remove any focus outline (section is not interactive)
        semanticTokens.bg.surface
      )}
      aria-labelledby="landing-cta"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Canon v1.4.5: Premium variant CTA block */}
      <div
        className={cn(
          "mx-auto max-w-5xl text-center relative overflow-hidden group",
          "rounded-2xl sm:rounded-3xl",
          "p-6 sm:p-8 md:p-10 lg:p-12",
          // Premium: Black Luxury gradient
          semanticTokens.premium.bgGradient,
          "backdrop-blur-xl backdrop-saturate-[180%]",
          semanticTokens.premium.border, "border",
          // Layered shadow with depth
          "shadow-[0_8px_40px_-12px_hsl(220_30%_20%/0.25),inset_0_1px_0_hsl(0_0%_100%/0.1)]",
          // Hover subtle elevation
          "hover:shadow-[0_12px_50px_-12px_hsl(220_50%_30%/0.35),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
          // Canonical motion duration (BUG-004)
          semanticTokens.motion.standard
        )}
      >
        <div className="relative z-10 space-y-6 sm:space-y-7 md:space-y-8">
          <div className="space-y-3 sm:space-y-4">
            {/* Premium heading with enhanced typography */}
            <h2
              id="landing-cta"
              className={cn(
                semanticTokens.typography.displaySm, // 30px - Small display for premium feel (includes font-display)
                semanticTokens.premium.fg,
                "tracking-tight",
                "font-black", // Maximum weight (900) for premium impact
                "mx-auto max-w-3xl"
              )}
            >
              {t("ctaSection.headline")}
            </h2>
            <Text
              variant="lg"
              color="default"
              className={cn(
                "mx-auto max-w-2xl leading-relaxed",
                semanticTokens.premium.accent,
                "dark:text-slate-300 dark:text-opacity-90"
              )}
            >
              {t("ctaSection.description")}
            </Text>
          </div>

          <div className="flex justify-center">
            <div className="relative inline-block">
              <SignalDot signal="pulse" size="xs" color="mint" className="absolute -left-2 -top-2 z-10" />
              <Button
                size="lg"
                className={cn(
                  // Touch targets ≥44px (BUG-027)
                  "min-h-[44px] h-12 sm:h-14",
                  "px-6 sm:px-8",
                  // Responsive text size (BUG-030)
                  "text-base sm:text-lg font-semibold rounded-full",
                  "w-full sm:w-auto", // Full width on mobile
                  semanticTokens.accent.primary,
                  semanticTokens.accent.hover,
                  semanticTokens.fg.inverted,
                  "shadow-lg hover:shadow-xl hover:-translate-y-0.5",
                  semanticTokens.motion.standard,
                  // Touch manipulation (BUG-029)
                  "touch-manipulation"
                )}
                onClick={() => navigate("/waitlist")}
              >
                {t("cta.primary")}
              </Button>
            </div>
          </div>

          {/* Invite note with proper spacing after buttons - extra margin for premium feel */}
          <Text
            variant="caption"
            color="default"
            className={cn(
              semanticTokens.premium.accent,
              "text-center",
              "dark:text-slate-300 dark:text-opacity-80",
              // Override space-y with explicit premium spacing
              "!mt-8 sm:!mt-10 md:!mt-12"
            )}
          >
            {t("ctaSection.inviteNote")}
          </Text>
        </div>

        {/* Specular highlight Liquid Glass signature */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent z-20 rounded-t-2xl sm:rounded-t-3xl" />

        {/* Decorative background element */}
        <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-blue-100/60 via-transparent to-transparent" />
      </div>
    </section>
  )
}
